import React, { useState } from 'react';
import { X, Maximize2, Minimize2 } from 'lucide-react';

/**
 * Tela cheia, a pedido e lembrada — por modal.
 *
 * A chave é do modal, e não uma só para o sistema: quem expande a ficha das
 * etapas para ver as tabelas inteiras não quer a confirmação de exclusão
 * cobrindo a tela na próxima vez. A escolha fica no navegador, que é
 * conveniência de quem usa e não dado; sem armazenamento, só abre em janela.
 */
export const useTelaCheia = (chave) => {
  const nome = `telaCheia:${chave}`;
  const [telaCheia, setTelaCheia] = useState(() => {
    try { return localStorage.getItem(nome) === '1'; } catch { return false; }
  });
  const alternar = () => setTelaCheia(v => {
    try { localStorage.setItem(nome, v ? '0' : '1'); } catch { /* sem armazenamento */ }
    return !v;
  });
  return [telaCheia, alternar];
};

/**
 * O botão de expandir. No celular os modais já ocupam a largura toda e o botão
 * seria um alvo a mais do lado do X, então ele só aparece do `sm` para cima.
 */
export const BotaoTelaCheia = ({ telaCheia, onAlternar, className = '' }) => (
  <button
    type="button"
    onClick={onAlternar}
    className={`hidden sm:block p-1 text-slate-400 hover:text-slate-600 transition-colors ${className}`}
    title={telaCheia ? 'Sair da tela cheia' : 'Tela cheia'}
    aria-label={telaCheia ? 'Sair da tela cheia' : 'Tela cheia'}
    aria-pressed={telaCheia}
  >
    {telaCheia ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
  </button>
);

/**
 * A MOLDURA DE TODO MODAL.
 *
 * Cada modal desenhava a sua: fundo, largura, cabeçalho de altura própria (o
 * de Custos tinha p-6 e título text-xl), X de tamanho próprio. Tela cheia em
 * cada um seria dezesseis versões do mesmo botão, então a moldura virou uma.
 *
 * O corpo (`children`) mora numa área que rola por dentro. Um modal que tem
 * rodapé fixo continua podendo tê-lo: basta o seu corpo ser `flex-1
 * overflow-y-auto` e o rodapé vir depois — a área daqui é uma coluna flex.
 *
 * EM TELA CHEIA O CABEÇALHO ENCOLHE. O espaço vertical é justamente o que se
 * foi buscar ao expandir, e uma barra de título de três linhas o devolvia: o
 * subtítulo sobe para a linha do título, e os respiros e a fonte diminuem.
 */
const JanelaModal = ({
  chave,
  titulo,
  subtitulo = null,
  Icone = null,
  onFechar,
  fecharDesabilitado = false,
  // Largura da janela fora da tela cheia (classe do Tailwind).
  largura = 'sm:max-w-lg',
  // Os que abrem por cima de outro modal (cadastro rápido, numerar) sobem.
  camada = 'z-50',
  // Botões extras do cabeçalho, antes do expandir.
  acoes = null,
  children,
}) => {
  const [telaCheia, alternar] = useTelaCheia(chave);

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${camada} ${
      telaCheia ? 'p-0' : 'p-2 sm:p-4'
    }`}>
      <div className={`bg-white flex flex-col w-full ${
        telaCheia ? 'h-dvh max-h-dvh' : `rounded-lg shadow-xl max-w-full ${largura} max-h-[92dvh]`
      }`}>
        <div className={`flex items-center justify-between gap-3 border-b border-slate-200 shrink-0 ${
          telaCheia ? 'px-3 py-1.5' : 'px-4 py-3'
        }`}>
          <div className={`min-w-0 flex gap-2 ${telaCheia ? 'items-center' : 'items-start'}`}>
            {Icone && (
              <Icone size={telaCheia ? 15 : 18}
                className={`text-slate-500 shrink-0 ${telaCheia ? '' : 'mt-0.5'}`} />
            )}
            <div className={`min-w-0 ${telaCheia ? 'flex items-baseline gap-2' : ''}`}>
              <h3 className={`font-semibold text-slate-900 truncate ${
                telaCheia ? 'text-sm' : 'text-base sm:text-lg'
              }`}>
                {titulo}
              </h3>
              {subtitulo && (
                <div className="text-xs text-slate-500 truncate">{subtitulo}</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {acoes}
            <BotaoTelaCheia telaCheia={telaCheia} onAlternar={alternar} />
            <button
              type="button"
              onClick={onFechar}
              disabled={fecharDesabilitado}
              className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label="Fechar"
            >
              <X size={telaCheia ? 16 : 20} />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 flex flex-col overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

export default JanelaModal;
